import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';
import toast from 'react-hot-toast';
import { IBacklink } from '../store/topicApi';

interface BacklinksModalProps {
  isOpen: boolean;
  onClose: () => void;
  backlinks: IBacklink[];
  loading?: boolean;
  error?: string;
  title: string;
}

const BacklinksModal: React.FC<BacklinksModalProps> = ({
  isOpen,
  onClose,
  backlinks,
  loading = false,
  error,
  title
}) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const modalContent = (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black bg-opacity-50 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden animate-scale-in">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">
            Backlinks for: {title}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          {loading && (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              <span className="ml-4 text-gray-600">Generating backlinks...</span>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-600">{error}</p>
            </div>
          )}

          {!loading && !error && backlinks.length === 0 && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
              <p className="text-gray-600">No backlinks found.</p>
            </div>
          )}

          {!loading && !error && backlinks.length > 0 && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600 mb-4">
                Found {backlinks.length} potential discussion{backlinks.length !== 1 ? 's' : ''} for backlink opportunities:
              </p>
              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-yellow-700">
                      <strong>Note:</strong> Suggested comments may not always be relevant or may contain mistakes. Please review and customize them before posting to ensure they provide genuine value to the discussion.
                    </p>
                  </div>
                </div>
              </div>
              {backlinks.map((backlink, index) => (
                <div
                  key={index}
                  className="border border-gray-200 rounded-lg p-5 hover:border-blue-400 hover:shadow-md transition-all"
                >
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="text-lg font-semibold text-gray-900 flex-1 pr-4">
                      {index + 1}. {backlink.title}
                    </h3>
                  </div>

                  <a
                    href={backlink.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 hover:underline text-sm mb-3 block break-all"
                  >
                    {backlink.url}
                  </a>

                  <div className="bg-gray-50 rounded-md p-4 mt-3">
                    <p className="text-sm font-medium text-gray-700 mb-2">
                      Suggested Comment:
                    </p>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      {backlink.comment}
                    </p>
                  </div>

                  <div className="mt-3 flex gap-2">
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(backlink.url);
                        toast.success('URL copied to clipboard!');
                      }}
                      className="text-xs bg-blue-100 hover:bg-blue-200 text-blue-700 px-3 py-1 rounded transition-colors"
                    >
                      Copy URL
                    </button>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(backlink.comment);
                        toast.success('Comment copied to clipboard!');
                      }}
                      className="text-xs bg-green-100 hover:bg-green-200 text-green-700 px-3 py-1 rounded transition-colors"
                    >
                      Copy Comment
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex justify-end p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};

export default BacklinksModal;